'use client'

import React from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import type { ServiceItem } from '@/types/interfaces/service-item'
import { textFadeInVariantsNoTransition } from '@/lib/animations'
import Product from './Product'
import classes from './Shelf.module.css'

export interface ShelfData {
    id?: string
    title?: string | null
    /** Optional “See all” destination for this shelf rail. */
    href?: string | null
    services?: ServiceItem[]
}

export interface ShelfProps {
    shelf: ShelfData
    getServiceHref?: (service: ServiceItem) => string | undefined
}

/** One titled product rail; owns its own enter motion. */
export default function Shelf({ shelf, getServiceHref }: ShelfProps) {
    const title = shelf.title?.trim() || null
    const seeAllHref = shelf.href?.trim() || null
    const services = (shelf.services ?? []).filter((service) => service?.id != null && service?.title)

    if (services.length === 0) return null

    return (
        <motion.div
            className={classes.root}
            data-shelf={shelf.id}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px 0px' }}
            variants={textFadeInVariantsNoTransition}
        >
            {title ? (
                <div className={classes.header}>
                    <h3 className={classes.heading}>{title}</h3>
                    {seeAllHref ? (
                        <Link href={seeAllHref} prefetch={false} className={classes.seeAll}>
                            See all
                        </Link>
                    ) : null}
                </div>
            ) : null}
            <div className={classes.scroller} aria-label={title ?? 'Products'}>
                {services.map((service, index) => (
                    <Product key={`${service.id}-${index}`} service={service} getServiceHref={getServiceHref} />
                ))}
            </div>
        </motion.div>
    )
}
